import { useState } from "react";
import { useAuth } from "@/App";
import { enablePushForUser } from "@/utils/pushNotifications";
import { Bell, Share } from "lucide-react";

/**
 * Dismissible banner for iOS push notification states:
 * - ios-pwa-tap-to-enable: user is in a PWA on iOS 16.4+, needs to tap to grant permission
 * - ios-safari-install-first: user is in Safari, needs to Add to Home Screen first
 */
const PushEnableBanner = () => {
  const { token, pushBannerState, setPushBannerState } = useAuth();
  const [loading, setLoading] = useState(false);

  if (!pushBannerState) return null;

  const dismiss = () => setPushBannerState(null);

  const handleEnable = async () => {
    setLoading(true);
    try {
      const result = await enablePushForUser(token);
      if (result.status === 'subscribed') {
        dismiss();
      } else if (result.status === 'denied') {
        dismiss();
      }
    } catch {
      dismiss();
    } finally {
      setLoading(false);
    }
  };

  if (pushBannerState === 'ios-pwa-tap-to-enable') {
    return (
      <div className="mx-4 mt-2 mb-1 p-3.5 rounded-2xl bg-[#1C2420] animate-fade-in" data-testid="push-enable-banner">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-[#768970]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Bell className="w-4 h-4 text-[#768970]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-white/85 font-medium" style={{ fontFamily: 'Outfit' }}>
              Turn on notifications for HeyMoji
            </p>
            <p className="text-xs text-white/40 mt-0.5">
              Get notified when you receive messages and payments
            </p>
            <div className="flex gap-2 mt-2.5">
              <button
                className="px-4 py-1.5 rounded-lg bg-[#768970] text-white text-xs font-medium tap-scale disabled:opacity-50"
                onClick={handleEnable}
                disabled={loading}
                data-testid="push-enable-btn"
              >
                {loading ? "Enabling..." : "Enable"}
              </button>
              <button
                className="px-3 py-1.5 rounded-lg bg-white/5 text-white/40 text-xs tap-scale"
                onClick={dismiss}
                data-testid="push-dismiss-btn"
              >
                Not now
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (pushBannerState === 'ios-safari-install-first') {
    return (
      <div className="mx-4 mt-2 mb-1 p-3.5 rounded-2xl bg-[#1C2420] animate-fade-in" data-testid="push-install-banner">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-[#768970]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Bell className="w-4 h-4 text-[#768970]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-white/85 font-medium" style={{ fontFamily: 'Outfit' }}>
              Add HeyMoji to your home screen to get notifications
            </p>
            <div className="flex items-center gap-3 mt-2.5 text-white/50">
              <div className="flex items-center gap-1.5 text-xs">
                <Share className="w-3.5 h-3.5" />
                <span>Tap Share</span>
              </div>
              <span className="text-white/20">then</span>
              <span className="text-xs">Add to Home Screen</span>
            </div>
            <button
              className="mt-2 px-3 py-1.5 rounded-lg bg-white/5 text-white/40 text-xs tap-scale"
              onClick={dismiss}
              data-testid="push-install-dismiss-btn"
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default PushEnableBanner;
